import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Download } from 'lucide-react';

// Helper functions (same as before)
const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
};

const getCatalogIdFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('catalogid');
    if (q) return q;
    const parts = window.location.pathname.split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    return /^\d+$/.test(last) ? last : null;
};

const ShowImagePreview = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [catalogId, setCatalogId] = useState(() => getCatalogIdFromUrl() || '1');
    const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [imageErrors, setImageErrors] = useState(new Set());

    const token = getCookie("token") || getCookie("authToken");
    const userid = getCookie("userid") || getCookie("userId");

    // Raw items
    const items = Array.isArray(data?.data) ? data.data : [];

    // Group images by catalogstructureid
    const groupedImages = useMemo(() => {
        const groupsMap = {};
        items
            .filter(item => item.active && item.filepath)
            .forEach(item => {
                const key = item.catalogstructureid;
                if (!groupsMap[key]) {
                    groupsMap[key] = {
                        id: key,
                        name: item.catalogstructurename,
                        images: []
                    };
                }
                groupsMap[key].images.push({
                    url: item.filepath.trim(), // trim whitespace in URL!
                    id: item.id,
                    name: item.name,
                    remark: item.remark,
                    brand: item.catalogstructurename,
                    filename: item.filename,
                    position: item.position
                });
            });

        // Sort images within each group by position
        Object.values(groupsMap).forEach(group => {
            group.images.sort((a, b) => a.position - b.position);
        });

        // Return as array, sorted by group name or id
        return Object.values(groupsMap).sort((a, b) => a.name.localeCompare(b.name));
    }, [items]);

    const totalGroups = groupedImages.length;
    const currentGroup = groupedImages[currentGroupIndex] || null;
    const currentImages = currentGroup?.images || [];
    const totalImagesInGroup = currentImages.length;

    const fetchCatalog = async (cid) => {
        setLoading(true);
        setError(null);
        setImageErrors(new Set());
        
        try {
            const res = await fetch('https://api.dqr-display-tms.bonrix.in/api/CatalogInfoOperation/SubUser/Search', {
                method: 'POST',
                headers: {
                    'accept': 'text/plain',
                    'Content-Type': 'application/json-patch+json',
                    'UserId': userid,
                    'X-Authorization': token,
                },
                body: JSON.stringify({
                    catalogid: String(cid),
                    ispageload: true,
                    pagesize: 1000,
                    currentpageno: 0,
                    withoutpagination: true,
                    pagedirection: 'first'
                })
            });
            
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            const json = await res.json();
            setData(json);
            setCurrentGroupIndex(0);
            setCurrentImageIndex(0);
        } catch (err) {
            setError('Failed to fetch Catalogue: ' + err.message);
            console.error('Catalogue fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCatalog(catalogId);
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (totalGroups === 0 || totalImagesInGroup === 0) return;
            
            switch(e.key) {
                case 'ArrowLeft':
                    setCurrentImageIndex(p => (p - 1 + totalImagesInGroup) % totalImagesInGroup);
                    break;
                case 'ArrowRight':
                    setCurrentImageIndex(p => (p + 1) % totalImagesInGroup);
                    break;
                case 'ArrowUp':
                    setCurrentGroupIndex(p => (p - 1 + totalGroups) % totalGroups);
                    setCurrentImageIndex(0); // reset image index when changing group
                    break;
                case 'ArrowDown':
                    setCurrentGroupIndex(p => (p + 1) % totalGroups);
                    setCurrentImageIndex(0);
                    break;
                case 'd':
                case 'D':
                    if (totalImagesInGroup >= 2) {
                        setCurrentImageIndex(1);
                    } else {
                        alert('Second image not available in this group!');
                    }
                    break;
            }
        };
        
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [totalGroups, totalImagesInGroup]);

    const handleSubmit = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        fetchCatalog(catalogId);
    };

    const handleImageError = (imgIndex) => {
        // We track by group + image index, but since we reset on group change, simple index is ok
        setImageErrors(prev => new Set([...prev, `${currentGroupIndex}-${imgIndex}`]));
    };

    const currentImage = currentImages[currentImageIndex];

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="container mx-auto px-4 py-8">
                <div className="bg-white rounded-lg shadow-lg">
                    <div className="flex justify-between items-center p-6 border-b">
                        <h1 className="text-2xl font-bold text-gray-800">Catalogue Image Preview</h1>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Catalogue ID"
                                value={catalogId}
                                onChange={(e) => setCatalogId(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                            />
                            <button 
                                onClick={handleSubmit}
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Loading...' : 'Load'}
                            </button>
                        </div>
                    </div>

                    <div className="p-6">
                        {error && (
                            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                                {error}
                            </div>
                        )}

                        {!loading && groupedImages.length === 0 && !error && (
                            <div className="p-4 bg-blue-100 border border-blue-400 text-blue-700 rounded">
                                No images found for this Catalogue.
                            </div>
                        )}

                        {loading && (
                            <div className="flex justify-center items-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                            </div>
                        )}

                        {!loading && groupedImages.length > 0 && (
                            <div className="flex flex-col items-center">
                                {/* Group Navigation: Up = Previous Group, Down = Next Group */}
                                <div className="mb-4 flex gap-4">
                                    <button 
                                        onClick={() => {
                                            setCurrentGroupIndex(p => (p - 1 + totalGroups) % totalGroups);
                                            setCurrentImageIndex(0);
                                        }}
                                        disabled={totalGroups <= 1}
                                        className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all"
                                        title="Previous Group"
                                    >
                                        <ChevronUp size={24} />
                                    </button>
                                </div>

                                {/* Group Info */}
                                {currentGroup && (
                                    <div className="mb-4 text-center">
                                        <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full font-semibold">
                                            {currentGroup.name} ({currentImages.length} images)
                                        </span>
                                        <div className="text-sm text-gray-500 mt-1">
                                            Group {currentGroupIndex + 1} of {totalGroups}
                                        </div>
                                    </div>
                                )}

                                {/* Image Navigation */}
                                <div className="relative flex items-center justify-center mb-4 max-w-4xl w-full">
                                    <button 
                                        onClick={() => setCurrentImageIndex(p => (p - 1 + totalImagesInGroup) % totalImagesInGroup)}
                                        disabled={totalImagesInGroup <= 1}
                                        className="absolute left-0 z-10 p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all"
                                        title="Previous Image"
                                        style={{transform: 'translateX(-50%)'}}
                                    >
                                        <ChevronLeft size={24} />
                                    </button>

                                    <div className="mx-16 max-w-2xl w-full">
                                        {imageErrors.has(`${currentGroupIndex}-${currentImageIndex}`) ? (
                                            <div className="bg-gray-200 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                                                <div className="text-gray-500 text-lg mb-2">Image not available</div>
                                                <div className="text-gray-400 text-sm">{currentImage?.filename}</div>
                                            </div>
                                        ) : (
                                            <img
                                                src={currentImage?.url}
                                                alt={`${currentImage?.brand} ${currentImage?.name}`}
                                                className="w-full max-h-96 object-contain rounded-lg shadow-md bg-white border"
                                                onError={() => handleImageError(currentImageIndex)}
                                            />
                                        )}
                                    </div>

                                    <button 
                                        onClick={() => setCurrentImageIndex(p => (p + 1) % totalImagesInGroup)}
                                        disabled={totalImagesInGroup <= 1}
                                        className="absolute right-0 z-10 p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all"
                                        title="Next Image"
                                        style={{transform: 'translateX(50%)'}}
                                    >
                                        <ChevronRight size={24} />
                                    </button>
                                </div>

                                {/* Next Group button placed directly below the image */}
                                <div className="mb-6">
                                    <button 
                                        onClick={() => {
                                            setCurrentGroupIndex(p => (p + 1) % totalGroups);
                                            setCurrentImageIndex(0);
                                        }}
                                        disabled={totalGroups <= 1}
                                        className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all"
                                        title="Next Group"
                                    >
                                        <ChevronDown size={24} />
                                    </button>
                                </div>

                                {/* Image Info */}
                                {currentImage && (
                                    <div className="mb-6 text-center">
                                        <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                            {currentImage.remark}
                                            {currentImageIndex === 1 && (
                                                <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                                                    2nd Image
                                                </span>
                                            )}
                                        </h3>
                                        <p className="text-gray-600 mb-1">Position: {currentImage.position}</p>
                                        <p className="text-gray-500 text-sm">ID: {currentImage.id}</p>
                                    </div>
                                )}

                            

                                {/* Thumbnails for current group only */}
                                {currentImages.length > 1 && (
                                    <div className="mt-6 flex gap-2 overflow-x-auto max-w-full pb-2">
                                        {currentImages.map((img, idx) => (
                                            <button
                                                key={img.id}
                                                onClick={() => setCurrentImageIndex(idx)}
                                                className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden ${
                                                    currentImageIndex === idx 
                                                        ? 'border-blue-500 ring-2 ring-blue-200' 
                                                        : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                            >
                                                {imageErrors.has(`${currentGroupIndex}-${idx}`) ? (
                                                    <div className="w-full h-full bg-gray-200 flex items-center justify-center text-xs text-gray-500">
                                                        N/A
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={img.url}
                                                        alt={`${img.brand} ${img.name}`}
                                                        className="w-full h-full object-cover"
                                                        onError={() => handleImageError(idx)}
                                                    />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShowImagePreview;